import type { Logger } from "@expert/logging";
import type { KyInstance } from "ky";

import type { SalesAnalytics } from "../sales-context/SalesContext.types";

import { useProductOfferStore } from "../stores/useProductOfferStore";
import { SaleProgress, SalesProvider } from "../types/types-sales";
import { buildEnrollmentOutcomeLog } from "./enrollment.helpers";

export const EnrollmentStatus = {
    Error: "ERROR",
    Success: "SUCCESS",
    Failure: "FAILURE",
} as const;
export type EnrollmentStatus = (typeof EnrollmentStatus)[keyof typeof EnrollmentStatus];

export interface Enrollment {
    sessionId: string;
    partner: string;
    productOfferId: string;
    productSku: string;
    confirmationCode?: string;
    [key: string]: unknown;
}

export interface EnrollmentResponse {
    enrollmentStatus: string;
    productSku: string;
    reason?: string;
}

export const createEnrollment = async (
    client: KyInstance,
    logger: Logger,
    analytics: SalesAnalytics,
    enrollment: Enrollment,
) => {
    const childLogger = logger.child({ module: "enrollment", productOfferId: enrollment.productOfferId });
    analytics.startDynatraceAction("SaleEnrollment", enrollment.productOfferId);
    try {
        childLogger.info("Making enrollment request");
        const result = await client.post("v2/enrollment", { json: enrollment }).json<EnrollmentResponse>();
        childLogger.info("Finished enrollment request");

        childLogger.info(buildEnrollmentOutcomeLog(enrollment, result), "Enrollment outcome");

        void analytics.dispatcher.dispatchBusinessEvent("GetEnrollmentStatus", {
            ProductOfferId: enrollment.productOfferId,
            enrollmentStatus: result.enrollmentStatus,
            StatusOrigin: "ASP",
            productSku: result.productSku,
            confirmationCode: enrollment.confirmationCode,
        });

        const { getProducts, getProductInfo } = useProductOfferStore.getState();
        const enrolledProducts = getProducts(enrollment.sessionId)
            .filter((p) => p.saleProgress === SaleProgress.Success)
            .map((p) => ({
                productSku: p.productSku,
                productOfferId: p.id,
                enrollmentStatus: p.saleProgress,
                statusOrigin: p.salesProvider,
                displayName: getProductInfo(enrollment.sessionId, p.productSku)?.displayName,
            }));
        const productInfo = getProductInfo(enrollment.sessionId, enrollment.productSku);
        const newEnrollment = {
            enrollmentStatus: result.enrollmentStatus === "SUCCESS" ? SaleProgress.Success : SaleProgress.Failure,
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            statusOrigin: SalesProvider.ASP,
            displayName: productInfo?.displayName,
        };
        analytics.sendToHoneycomb(enrollment.sessionId, {
            enrollment: enrolledProducts.concat([newEnrollment]),
        });

        const isSuccess = result.enrollmentStatus === "SUCCESS";
        const eventName = isSuccess ? "EnrollmentSuccess" : "EnrollmentFailure";

        analytics.trackFullStoryEvent(eventName, {
            productSku: enrollment.productSku,
            productOfferId: enrollment.productOfferId,
            sessionId: enrollment.sessionId,
            partner: enrollment.partner,
            enrollmentStatus: result.enrollmentStatus,
            statusOrigin: "asp",
        });

        analytics.endDynatraceAction(
            "SaleEnrollment",
            isSuccess ? undefined : { taskId: enrollment.productOfferId, error: eventName },
        );

        return result;
    } catch (error: unknown) {
        void analytics.dispatcher.dispatchBusinessEvent("EnrollmentError", {
            Status: "LAMBDA_ERROR",
            ProductOfferId: enrollment.productOfferId,
            Error: error,
            productSku: enrollment.productSku,
        });

        childLogger.warn({ error, enrollment }, "enrollment api call failed");
        analytics.endDynatraceAction("SaleEnrollment", {
            taskId: enrollment.productOfferId,
            error: "EnrollmentError",
        });
        return { enrollmentStatus: EnrollmentStatus.Error };
    }
};
